export function flattenMenuTree(
  nodes = [],
  depth = 0,
  parent = null,
  acc = [],
) {
  nodes.forEach((node) => {
    acc.push({
      id: node.id,
      label: node.label,
      parent_id: node.parent_id ?? null,
      depth,
      parentLabel: parent?.label || "",
      type: getMenuItemType(node),
      hasChildren: Array.isArray(node.children) && node.children.length > 0,
    });

    if (node.children?.length) {
      flattenMenuTree(node.children, depth + 1, node, acc);
    }
  });

  return acc;
}

export function getDescendantIds(nodes = [], id) {
  const target = findNode(nodes, id);

  if (!target) return new Set();

  const ids = new Set();

  const walk = (children = []) => {
    children.forEach((child) => {
      ids.add(child.id);

      if (child.children?.length) {
        walk(child.children);
      }
    });
  };

  walk(target.children || []);

  return ids;
}

export function findNode(nodes = [], id) {
  for (const node of nodes) {
    if (node.id === id) return node;

    if (node.children?.length) {
      const found = findNode(node.children, id);
      if (found) return found;
    }
  }

  return null;
}

export function filterMenuTree(nodes = [], query = "") {
  const term = String(query || "")
    .trim()
    .toLowerCase();

  if (!term) return nodes;

  const match = (node) => {
    const link = getMenuItemHref(node);

    return (
      String(node.label || "")
        .toLowerCase()
        .includes(term) ||
      String(node.title_attr || "")
        .toLowerCase()
        .includes(term) ||
      String(node?.page?.title || "")
        .toLowerCase()
        .includes(term) ||
      String(node?.page?.slug || "")
        .toLowerCase()
        .includes(term) ||
      String(link || "")
        .toLowerCase()
        .includes(term)
    );
  };

  const filter = (items = []) =>
    items
      .map((node) => {
        const children = filter(node.children || []);

        if (match(node) || children.length) {
          return {
            ...node,
            children,
          };
        }

        return null;
      })
      .filter(Boolean);

  return filter(nodes);
}

export function collectFavorites(nodes = []) {
  const out = [];

  const walk = (items = []) => {
    items.forEach((node) => {
      if (node.is_favorite) out.push(node);

      if (node.children?.length) {
        walk(node.children);
      }
    });
  };

  walk(nodes);

  return out;
}

export function getMenuItemHref(node = {}) {
  if (node?.page?.slug) return `/pages/${node.page.slug}`;
  if (node?.url) return node.url;

  return "";
}

export function getMenuItemType(node = {}) {
  const linkType = node.link_type || "";

  // Priorité absolue au type déclaré en base
  if (linkType === "none") return "folder";
  if (linkType === "page") return "page";
  if (linkType === "url") return "url";

  // Fallback pour les anciennes données sans link_type
  const hasChildren = Array.isArray(node.children) && node.children.length > 0;
  const hasPage = !!(node.page?.id || node.page_id);
  const hasUrl = !!String(node.url || "").trim();

  if (hasPage) return "page";
  if (hasUrl) return "url";
  if (hasChildren) return "folder";

  return "folder";
}

export function getTypeLabel(type = "folder") {
  if (type === "page") return "Page du site";
  if (type === "url") return "Lien personnalisé";

  return "Dossier";
}

export function getLevelLabel(depth = 0, type = "folder") {
  if (depth === 0) {
    if (type === "folder") return "Onglet / dossier";
    if (type === "page") return "Onglet vers page";

    return "Onglet vers lien";
  }

  if (type === "folder") return "Dossier";
  if (type === "page") return "Page";

  return "Lien";
}

export function computeMenuStats(tree = []) {
  const flat = flattenMenuTree(tree);

  return {
    total: flat.length,
    roots: flat.filter((item) => item.depth === 0).length,
    folders: flat.filter((item) => item.type === "folder").length,
    links: flat.filter((item) => item.type === "page" || item.type === "url")
      .length,
  };
}

export function buildOrderedPayload(parentId, orderedIds = []) {
  return orderedIds.map((id, index) => ({
    id,
    parent_id: parentId ?? null,
    order_index: index,
  }));
}
